<template>
  <div class="login-page">
    <!-- Top navbar -->
    <div class="login-nav">
      <div class="login-nav__logo">
        <img src="/images/taxpod_logo.png" alt="taxPOD" class="login-nav__logo-img" />
      </div>
    </div>

    <!-- Main content -->
    <div class="login-main">
      <!-- Left: Blue panel -->
      <div class="login-left">
        <div class="login-left__bg">
          <div class="geo geo--1"></div>
          <div class="geo geo--2"></div>
          <div class="geo geo--3"></div>
          <div class="geo geo--4"></div>
        </div>
        <div class="login-left__content">
          <h1 class="login-left__title">
            Create a New<br />
            Secure Password<br />
            for Your Account
          </h1>
        </div>
      </div>

      <!-- Right: Form area -->
      <div class="login-right">
        <div class="login-right__wave">
          <svg viewBox="0 0 600 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M600 800V0C600 0 580 200 500 350C420 500 300 550 250 650C200 750 300 800 600 800Z" fill="rgba(56,189,248,0.06)"/>
            <path d="M600 800V100C600 100 560 300 480 420C400 540 320 580 280 680C240 780 350 800 600 800Z" fill="rgba(56,189,248,0.04)"/>
          </svg>
        </div>

        <!-- Valid Token: Reset form -->
        <div class="login-card" v-if="this.checkTokenValidity()">
          <div class="login-card__icon-wrap">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="28" height="28">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <h2 class="login-card__title">Reset Password</h2>
          <p class="login-card__subtitle">Enter your new password below</p>

          <!-- Success message -->
          <transition name="fade">
            <div v-if="passwordResetMessage" class="login-alert login-alert--success">
              <v-icon size="18" color="#16a34a" class="login-alert__icon">mdi-check-circle-outline</v-icon>
              <span class="login-alert__text">{{ passwordResetMessage }}</span>
            </div>
          </transition>

          <!-- API error -->
          <transition name="fade">
            <div v-if="api.isError" class="login-alert login-alert--error">
              <v-icon size="18" color="#dc2626" class="login-alert__icon">mdi-alert-circle-outline</v-icon>
              <span class="login-alert__text">{{ api.error }}</span>
            </div>
          </transition>

          <!-- Validation checklist -->
          <div class="validation-list" v-show="isPasswordFilled">
            <div
              class="validation-item"
              :class="{ 'validation-item--pass': item.color === 'success' }"
              v-for="(item, key) in validationPrompts"
              :key="key"
            >
              <div class="validation-item__dot">
                <svg v-if="item.color === 'success'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor" width="10" height="10">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
              <span class="validation-item__text">{{ item.label }}</span>
            </div>
          </div>

          <!-- New Password -->
          <div class="field">
            <label class="field__label">New Password</label>
            <div class="field__input-wrap">
              <input
                :type="show1 ? 'text' : 'password'"
                class="field__input"
                placeholder="Enter new password"
                v-model="form.password"
                @keyup="passwordPromptValidation()"
              />
              <button class="field__toggle" @click="show1 = !show1" type="button">
                <v-icon size="18" color="var(--app-text-faint)">{{ show1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
            </div>
            <span class="field__hint">At least 8 characters with uppercase, lowercase, number and special character.</span>
          </div>

          <!-- Confirm Password -->
          <div class="field">
            <label class="field__label">Confirm New Password</label>
            <div class="field__input-wrap">
              <input
                :type="show2 ? 'text' : 'password'"
                class="field__input"
                placeholder="Re-enter new password"
                v-model="form.passwordConfirmation"
                @keyup="confirmationPasswordPromptValidation()"
                @keyup.enter="fetch()"
              />
              <button class="field__toggle" @click="show2 = !show2" type="button">
                <v-icon size="18" color="var(--app-text-faint)">{{ show2 ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
            </div>
          </div>

          <!-- Submit -->
          <button
            class="login-btn"
            :disabled="api.isLoading"
            @click="fetch()"
          >
            <span v-if="!api.isLoading">Reset Password</span>
            <span v-else class="login-btn__loading">
              <v-progress-circular indeterminate size="18" width="2" color="white"></v-progress-circular>
            </span>
          </button>

          <div class="login-card__bottom">
            <a class="back-link" @click="cancel()">Back to Login</a>
          </div>
        </div>

        <!-- Invalid/Expired Token -->
        <div class="login-card" v-else>
          <div class="login-card__icon-wrap login-card__icon-wrap--error">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="28" height="28">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
            </svg>
          </div>
          <h2 class="login-card__title">Link Expired</h2>
          <p class="login-card__subtitle">This reset link is invalid or has expired. Please request a new one.</p>

          <button class="login-btn" @click="cancel()">
            Back to Home
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import Api from "@/objects/api";
import HrdcData from "@/services/hrdc/objects/globalData";

export default {
  computed: {
    isPasswordFilled() {
      return this.form.password;
    },
  },
  data: () => ({
    form: {
      password: null,
      passwordConfirmation: null,
      token: null,
    },
    api: new Api(),
    show1: false,
    show2: false,
    isResetLinkValid: true,
    hrdcData: new HrdcData(),
    passwordResetMessage: null,
    validationPrompts: [
      {
        label: "Uppercase",
        slug: "uppercase",
        color: "",
      },
      {
        label: "Lowercase",
        slug: "lowercase",
        color: "",
      },
      {
        label: "At least 8 characters",
        slug: "at-least-8-char",
        color: "",
      },
      {
        label: "Number",
        slug: "number",
        color: "",
      },
      {
        label: "Special Character",
        slug: "special-char",
        color: "",
      },
      {
        label: "Confirmation Password Match",
        slug: "confirmation-password-match",
        color: "",
      },
    ],
  }),
  created() {
    this.api.setMethod(`POST`);
    this.api.setUrl(
      `${this.$service.sso}/v1/en/auth/login/password/confirm-reset-password`
    );
    this.api.setParams((this.form.token = this.$route.query.token));
    this.api.setCallbackCompleted((resp) => {
      if (resp.status === "success") {
        this.passwordResetMessage = resp.message;
        this.$router.push({ name: "PageGuestHome" });
      }
    });
  },
  methods: {
    fetch() {
      this.api.setParams(this.form);
      this.api.fetch();
    },
    checkTokenValidity() {
      var token = this.$route.query.token;
      try {
        const decoded = JSON.parse(atob(token));
        const currentTime = new Date().getTime();

        if (currentTime > decoded.exp) {
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    cancel() {
      this.$router.push({ name: "PageGuestHome" });
    },
    passwordPromptValidation() {
      this.isContainsUppercase();
      this.isContainsLowercase();
      this.isContains8Characters();
      this.isContainsNumbers();
      this.isContainsSpecialChar();
      this.isPasswordMatch();
    },
    confirmationPasswordPromptValidation() {
      this.isPasswordMatch();
    },
    isContainsUppercase() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "uppercase"
      );

      const match = /[A-Z]/.test(this.form.password);
      field.color = match ? "success" : "";
    },
    isContainsLowercase() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "lowercase"
      );

      const match = /[a-z]/.test(this.form.password);
      field.color = match ? "success" : "";
    },
    isContains8Characters() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "at-least-8-char"
      );

      field.color = this.form.password.length >= 8 ? "success" : "";
    },
    isContainsNumbers() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "number"
      );

      const match = /\d/.test(this.form.password);
      field.color = match ? "success" : "";
    },
    isContainsSpecialChar() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "special-char"
      );

      const match = !/^[a-zA-Z0-9]*$/.test(this.form.password);
      field.color = match ? "success" : "";
    },
    isPasswordMatch() {
      const field = this.validationPrompts.find(
        (item) => item.slug === "confirmation-password-match"
      );

      const match = this.form.password == this.form.passwordConfirmation;
      field.color = match ? "success" : "";
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .login-nav {
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .login-card {
  background: var(--app-card);
}

.dark-mode .login-card__icon-wrap {
  background: var(--app-accent-soft);
}

.dark-mode .login-card__icon-wrap--error {
  background: var(--app-danger-bg);
}

.dark-mode .login-card__title {
  color: var(--app-text);
}

.dark-mode .login-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .login-alert--error {
  background: var(--app-danger-bg);
}

.dark-mode .login-alert--success {
  background: var(--app-success-bg);
}

.dark-mode .validation-list {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .validation-item__dot {
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .validation-item__text {
  color: var(--app-text-faint);
}

.dark-mode .field__label {
  color: var(--app-text);
}

.dark-mode .field__input {
  border: 1.5px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .field__hint {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ===== PAGE ===== */
.login-page {
  /* `zoom: 1.1` below scales this box after layout, so a plain 100vh paints at
     110vh and the bottom tenth — the footer — falls off screen behind
     `overflow: hidden`. Dividing by the zoom makes the painted height 100vh. */
  height: calc(100vh / 1.1);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f4f8;
  zoom: 1.1;
  overflow: hidden;
}

/* ===== TOP NAVBAR ===== */
.login-nav {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 32px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
  z-index: 10;
}

.login-nav__logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.login-nav__logo-img {
  height: 32px;
  object-fit: contain;
}

/* ===== MAIN ===== */
.login-main {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* ===== LEFT BLUE PANEL ===== */
.login-left {
  flex: 0 0 42%;
  background: linear-gradient(160deg, #2c4a7c, #345a8c, #3a6a9e);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.login-left__bg {
  position: absolute;
  inset: 0;
}

.geo {
  position: absolute;
  background: rgba(255, 255, 255, 0.04);
}

.geo--1 {
  width: 300px;
  height: 300px;
  top: -50px;
  left: -80px;
  transform: rotate(45deg);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.geo--2 {
  width: 200px;
  height: 200px;
  top: 30%;
  left: 25%;
  transform: rotate(30deg);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.geo--3 {
  width: 250px;
  height: 250px;
  bottom: -60px;
  left: 10%;
  transform: rotate(60deg);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.geo--4 {
  width: 150px;
  height: 150px;
  top: 15%;
  right: -30px;
  transform: rotate(15deg);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.login-left__content {
  position: relative;
  z-index: 1;
  padding: 60px;
}

.login-left__title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.35;
  margin: 0;
}

/* ===== RIGHT PANEL ===== */
.login-right {
  flex: 1;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 40px;
}

.login-right__wave {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 60%;
  height: 100%;
  pointer-events: none;
}

.login-right__wave svg {
  width: 100%;
  height: 100%;
}

/* ===== CARD ===== */
.login-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #e4e8ec;
  position: relative;
  z-index: 1;
}

.login-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.login-card__icon-wrap--error {
  background: #fef2f2;
  color: #ef4444;
}

.login-card__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.login-card__subtitle {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0 0 28px;
  font-weight: 400;
  line-height: 1.5;
}

/* ===== ALERTS ===== */
.login-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.82rem;
  font-weight: 500;
}

.login-alert--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.login-alert--success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
}

.login-alert__icon {
  flex-shrink: 0;
}

.login-alert__text {
  line-height: 1.4;
}

/* ===== VALIDATION CHECKLIST ===== */
.validation-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.validation-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.validation-item__dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  color: #fff;
}

.validation-item--pass .validation-item__dot {
  background: #22c55e;
  border-color: #22c55e;
}

.validation-item__text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #94a3b8;
  transition: color 0.2s ease;
}

.validation-item--pass .validation-item__text {
  color: #16a34a;
}

/* ===== FORM FIELDS ===== */
.field {
  margin-bottom: 20px;
}

.field__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.field__input-wrap {
  position: relative;
}

.field__input {
  width: 100%;
  padding: 11px 42px 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field__input::placeholder {
  color: #b0b8c1;
}

.field__input:focus {
  border-color: #60b8e0;
  box-shadow: 0 0 0 3px rgba(96, 184, 224, 0.1);
}

.field__toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
}

.field__hint {
  display: block;
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 6px;
  line-height: 1.4;
}

/* ===== BUTTON ===== */
.login-btn {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #5ab8e0, #60c4ec);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
}

.login-btn:hover {
  background: linear-gradient(135deg, #4aa8d0, #55b8e2);
  box-shadow: 0 4px 14px rgba(90, 184, 224, 0.25);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.login-btn__loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== BOTTOM LINK ===== */
.login-card__bottom {
  text-align: center;
  margin-top: 20px;
}

.back-link {
  font-size: 0.8rem;
  color: #3a9fd4;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
}

.back-link:hover {
  text-decoration: underline;
}

/* ===== FADE TRANSITION ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .login-page {
    zoom: 1;
  }

  .login-main {
    flex-direction: column;
  }

  .login-left {
    flex: none;
    padding: 40px 28px;
    min-height: 180px;
  }

  .login-left__content {
    padding: 0;
  }

  .login-left__title {
    font-size: 1.3rem;
  }

  .login-right {
    padding: 28px 20px;
  }

  .login-card {
    max-width: 100%;
    padding: 28px 24px;
  }

  .validation-list {
    flex-direction: column;
    gap: 6px;
  }
}
</style>
